import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { apiErrorMessage } from "@/lib/api";
import { useCreateGuideCategory, useGuideCategories } from "@/hooks/api/guides";

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

const schema = z.object({
  name: z.string().min(1, "Required"),
  slug: z.string().min(1, "Required"),
});
type FormValues = z.infer<typeof schema>;

interface ManageGuideCategoriesDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Lightweight category manager scoped to the Guides page — unlike Flow
 * categories (which get their own /categories route), guide categories
 * are few enough (e.g. "Difficult Callers", "Billing FAQs") that a modal
 * quick-add is enough; no dedicated page needed.
 */
export function ManageGuideCategoriesDialog({
  open,
  onClose,
}: ManageGuideCategoriesDialogProps) {
  const { data, isLoading } = useGuideCategories();
  const createCategory = useCreateGuideCategory();
  const [slugTouched, setSlugTouched] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: "", slug: "" },
  });

  const onSubmit = async (values: FormValues) => {
    try {
      await createCategory.mutateAsync(values);
      toast.success("Category created.");
      form.reset({ name: "", slug: "" });
      setSlugTouched(false);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't create that category."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Guide categories</DialogTitle>
        </DialogHeader>

        {!isLoading && data && data.results.length > 0 && (
          <ul className="max-h-40 divide-y divide-slate-200 overflow-y-auto rounded-lg border border-slate-200">
            {data.results.map((cat) => (
              <li key={cat.id} className="px-3 py-2 text-sm">
                <span className="font-medium text-slate-900">{cat.name}</span>{" "}
                <span className="text-slate-400">/{cat.slug}</span>
              </li>
            ))}
          </ul>
        )}
        {!isLoading && data && data.results.length === 0 && (
          <p className="text-sm text-slate-500">No categories yet — add the first one below.</p>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3" noValidate>
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="e.g. Difficult Callers"
                      onChange={(e) => {
                        field.onChange(e);
                        if (!slugTouched) {
                          form.setValue("slug", slugify(e.target.value));
                        }
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="slug"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slug</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      onChange={(e) => {
                        setSlugTouched(true);
                        field.onChange(e);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" loading={createCategory.isPending}>
              Add category
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
